import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NoteIcon from "@mui/icons-material/Note";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SourceIcon from "@mui/icons-material/Source";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Typography } from "@mui/material";
import Link from "@mui/material/Link";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase";

const formatPhoneNumber = phoneNumber => {
  if (!phoneNumber) return "";
  const phoneNumberParsed = parsePhoneNumberFromString(phoneNumber, "FR");
  if (phoneNumberParsed) {
    return phoneNumberParsed.formatInternational();
  }
  return phoneNumber;
};

const phoneGetURI = phoneNumber => {
  if (!phoneNumber) return "";
  const phoneNumberParsed = parsePhoneNumberFromString(phoneNumber, "FR");
  if (phoneNumberParsed) {
    return phoneNumberParsed.getURI();
  }
  return phoneNumber;
};

const CustomerDetails = ({ open, handleClose, customer, customerId }) => {
  const [customerData, setCustomerData] = useState(customer);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCustomer = async () => {
      if (customerId) {
        setLoading(true);
        const customerDoc = doc(
          db,
          `users/${auth.currentUser.uid}/customers`,
          customerId
        );
        const docSnap = await getDoc(customerDoc);
        if (docSnap.exists()) {
          setCustomerData(docSnap.data());
        }
        setLoading(false);
      }
    };

    if (customerId && !customer) {
      fetchCustomer();
    } else {
      setCustomerData(customer);
    }
  }, [customerId, customer]);

  return (
    <Dialog open={open} onClose={handleClose}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle>{t("clients.title")}</DialogTitle>
          <Divider />
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Avatar
                sx={{ bgcolor: "primary.main", width: 56, height: 56, mb: 2 }}
              >
                {customerData?.firstname?.charAt(0)}
              </Avatar>
              <Typography variant="h6">{customerData?.firstname}</Typography>
            </Box>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={customerData?.firstname} secondary={t("clients.name")} />
              </ListItem>

              {customerData?.company && (
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText primary={customerData?.company} secondary={t("clients.company")} />
                </ListItem>
              )}

              <ListItem>
                <ListItemIcon>
                  <MailOutlineIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link href={`mailto:${customerData?.email}`}>
                      {customerData?.email}
                    </Link>
                  }
                  secondary={t("clients.email")}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <PhoneIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link href={`${phoneGetURI(customerData?.phone)}`}>
                      {formatPhoneNumber(customerData?.phone)}
                    </Link>
                  }
                  secondary={t("clients.phone")}
                />
              </ListItem>

              {customerData?.userAddress && (
                <ListItem>
                  <ListItemIcon>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary={customerData?.userAddress} secondary={t("clients.address")} />
                </ListItem>
              )}

              {customerData?.userNote && (
                <ListItem>
                  <ListItemIcon>
                    <NoteIcon />
                  </ListItemIcon>
                  <ListItemText primary={customerData?.userNote} secondary={t("clients.note")} />
                </ListItem>
              )}

              {customerData?.source && (
                <ListItem>
                  <ListItemIcon>
                    <SourceIcon />
                  </ListItemIcon>
                  <ListItemText primary={customerData?.source} secondary={t("clients.source")} />
                </ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              {t("common.cancel")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CustomerDetails;
